import React, { forwardRef } from 'react';

import type { InsertTableColumnLocation } from '@aline/common';
import { BaseMenuOption } from 'src/editor/toolbars/menus/BaseMenuOption';
import type { CommonMenuItemProps } from 'src/editor/toolbars/menus/types';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

interface InsertTableColumnMenuItemProps extends CommonMenuItemProps {
  columnLocation: InsertTableColumnLocation;
  numberOfColumns: number;
}

export const InsertTableColumnMenuItem = forwardRef(function InsertTableColumnMenuItem(
  props: InsertTableColumnMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element {
  const { closeRootMenu, columnLocation, numberOfColumns, ...rest } = props;
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const isMultipleColumns = numberOfColumns > 1;
  const label = isMultipleColumns
    ? `Insert ${numberOfColumns} columns ${columnLocation}`
    : `Insert column ${columnLocation}`;

  function insertColumns(): void {
    if (!editingMode || !currentUserId) return closeRootMenu();

    AuthorizedTransforms.insertTableColumns(editor, {
      authorId: currentUserId,
      columnLocation,
      editingMode,
    });
    ClientTransforms.focus(editor);
    closeRootMenu();
  }

  return <BaseMenuOption ref={ref} label={label} onClick={insertColumns} {...rest} />;
});
